if (!window.Maximize.loadedScript.includes("product-card.js")) {
  window.Maximize.loadedScript.push("product-card.js");

  /**
   * Shared helper — syncs swatch label styles and input state from a parsed
   * response document back into the live card wrapper.
   * @param {Document} dataHTML
   * @param {Element}  rootEl
   */
  function syncSwatchOptionStyles(dataHTML, rootEl) {
    rootEl.querySelectorAll(".swatch-option__label").forEach((label) => {
      const sourceLabel = dataHTML.querySelector(
        `.swatch-option__label[data-position="${label.dataset.position}"]`
      );
      if (!sourceLabel) return;

      if (sourceLabel.hasAttribute("style")) {
        label.setAttribute("style", sourceLabel.getAttribute("style"));
        if (label.hasAttribute("data-card-overflow-expanded")) label.style.removeProperty("display");
      }

      const input = label.previousElementSibling;
      const newInput = sourceLabel.previousElementSibling;
      if (input && newInput) {
        input.classList.toggle("disabled", newInput.classList.contains("disabled"));
        input.classList.toggle("selected", newInput.classList.contains("selected"));
        input.checked = newInput.classList.contains("selected");
      }
    });
  }

  /**
   * Reads a variant JSON blob, returning null when there is no variant to read — a missing
   * element, an empty body, or a literal `null` all mean "this option combination does not
   * exist". Callers must not keep the previously selected variant in that case: downstream
   * (the quantity selector's swatchChange listener) treats a variant as proof of a usable
   * quantity rule.
   * @param {Element|null} el
   * @returns {object|null}
   */
  function parseVariantInfo(el) {
    const raw = el && el.textContent ? el.textContent.trim() : "";
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // ProductCardSwatchOption
  // ---------------------------------------------------------------------------
  class ProductCardSwatchOption extends HTMLElement {
    static cacheData = {};

    constructor() {
      super();
      this.productCardWrapperEle = this.closest(".product-card__wrapper");
      this.productId = this.dataset.productId;
      this.sectionId = this.dataset.sectionId;
      this.optionId = this.dataset.optionId;
      this.productCardId = this.dataset.productCardId;
      this.productUrl = this.dataset.productUrl;
      this.effectImage = this.dataset.effectImage;
      this.isShowEDT = this.dataset.showEdt === "true";
      this.optionsSelectedValues = [];
      this.explicitSelectionEnabled = this.dataset.cardExplicitSelection === "true";
      this.optionCount = Number(this.dataset.cardOptionCount) || 0;
      this.optionCounts = (this.dataset.cardOptionCounts || "").split(",").map(Number);
      this.variantCount = Number(this.dataset.cardVariantCount) || 0;
      this.confirmedOptionPositions = new Set();
      this.selectionPending = false;
      this.selectionVerified = false;
      this.selectionError = false;
      this._swatchRequestId = 0;

      this.optionValueInputElements = Array.from(this.querySelectorAll(".variant-option__input"));
      this.optionLabelElements = Array.from(this.querySelectorAll(".swatch-option__label"));
      this.optionSelectElements = Array.from(this.querySelectorAll("[data-card-option-select]"));
      this.currentVariantEl = this.productCardWrapperEle.querySelector(".current-variant");
      this.currentVariant = parseVariantInfo(this.currentVariantEl);

      this.optionValueInputElements
        .filter((el) => el.classList.contains("selected"))
        .forEach((input) => {
          this.optionsSelectedValues[input.dataset.optionPosition - 1] = input.dataset.optionValueId;
        });
    }

    connectedCallback() {
      if (this._handleOptionClick) return;
      this._handleOptionClick = this.handleClickOptionLabel.bind(this);
      this._handleShowAllOptions = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const button = event.currentTarget;
        const overflowLabels = Array.from(button.closest("fieldset")?.querySelectorAll(".swatch-option__label[data-card-overflow-option]") || []);
        overflowLabels.forEach((label) => {
          label.setAttribute("data-card-overflow-expanded", "");
          label.style.removeProperty("display");
        });
        button.remove();
        overflowLabels[0]?.focus({ preventScroll: true });
      };
      this.showAllOptionButtons = Array.from(this.querySelectorAll("[data-card-show-all-options]"));
      this.showAllOptionButtons.forEach((button) => button.addEventListener("click", this._handleShowAllOptions));
      this._handleOptionKeydown = (event) => {
        if (event.key === " ") event.preventDefault();
      };
      this._handleOptionKeyup = (event) => {
        if (event.key === "Enter" || event.key === " ") this._handleOptionClick(event);
      };
      this.optionLabelElements.forEach((el) => {
        el.addEventListener("click", this._handleOptionClick);
        el.addEventListener("keydown", this._handleOptionKeydown);
        el.addEventListener("keyup", this._handleOptionKeyup);
      });
      this._handleOptionSelect = (event) => {
        const select = event.currentTarget;
        const input = this.optionValueInputElements.find((option) =>
          option.dataset.optionPosition === select.dataset.optionPosition &&
          option.dataset.optionValueId === select.value
        );
        if (input) this.selectOptionInput(input);
        else this.syncDropdownSelections();
      };
      this.optionSelectElements.forEach((select) => select.addEventListener("change", this._handleOptionSelect));
      if (this.explicitSelectionEnabled) {
        this.syncExplicitSelectionStyles();
        this.publishSelectionState();
      }
      this.syncDropdownSelections();
    }

    disconnectedCallback() {
      this._swatchRequestId += 1;
      this._swatchAbortController?.abort();
      this.selectionPending = false;
      this.selectionVerified = false;
      this.optionLabelElements.forEach((el) => {
        el.removeEventListener("click", this._handleOptionClick);
        el.removeEventListener("keydown", this._handleOptionKeydown);
        el.removeEventListener("keyup", this._handleOptionKeyup);
      });
      this.showAllOptionButtons.forEach((button) => button.removeEventListener("click", this._handleShowAllOptions));
      this.optionSelectElements.forEach((select) => select.removeEventListener("change", this._handleOptionSelect));
      this._handleOptionClick = null;
    }

    syncDropdownSelections() {
      this.optionSelectElements.forEach((select) => {
        Array.from(select.options).forEach((choice) => {
          if (!choice.value) return;
          const input = this.optionValueInputElements.find((option) =>
            option.dataset.optionPosition === select.dataset.optionPosition &&
            option.dataset.optionValueId === choice.value
          );
          const unavailable = input?.classList.contains("disabled");
          choice.textContent = choice.dataset.optionLabel + (unavailable ? " (" + select.dataset.unavailableLabel + ")" : "");
        });
        const selected = this.optionValueInputElements.find((input) =>
          input.dataset.optionPosition === select.dataset.optionPosition && input.checked
        );
        select.value = selected ? selected.dataset.optionValueId : "";
      });
    }

    hasCompleteExplicitSelection() {
      if (!this.explicitSelectionEnabled || this.optionCount < 1 || this.variantCount < 1 || this.variantCount > 250) return false;
      return Array.from({ length: this.optionCount }, (_, idx) => {
        const input = this.optionValueInputElements.find((option) =>
          Number(option.dataset.optionPosition) === idx + 1 &&
          option.dataset.optionValueId === this.optionsSelectedValues[idx]
        );
        return !!input && (this.optionCounts[idx] === 1 || this.confirmedOptionPositions.has(idx + 1));
      }).every(Boolean);
    }

    getSelectionState() {
      return {
        complete: this.selectionVerified && !this.selectionPending && this.hasCompleteExplicitSelection(),
        pending: this.selectionPending,
        error: this.selectionError,
        variantId: this.selectionVerified ? this.currentVariant?.id || null : null,
      };
    }

    publishSelectionState() {
      if (!this.explicitSelectionEnabled) return;
      this.productCardWrapperEle.dispatchEvent(new CustomEvent("ub:card-selection", { detail: this.getSelectionState() }));
    }

    syncExplicitSelectionStyles() {
      if (!this.explicitSelectionEnabled) return;
      this.optionValueInputElements.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        const confirmed = this.optionCounts[position - 1] === 1 || this.confirmedOptionPositions.has(position);
        const selected = confirmed && input.dataset.optionValueId === this.optionsSelectedValues[position - 1];
        input.checked = selected;
        input.classList.toggle("selected", selected);
      });
      this.optionLabelElements.forEach((label) => {
        label.setAttribute("role", "button");
        label.setAttribute("aria-pressed", String(label.previousElementSibling.checked));
      });
    }

    variantMatchesSelection(variant) {
      if (!variant) return true;
      if (!Array.isArray(variant.options) || variant.options.length !== this.optionCount || !variant.id) return false;
      return this.optionsSelectedValues.every((optionId, idx) => {
        const input = this.optionValueInputElements.find((option) =>
          Number(option.dataset.optionPosition) === idx + 1 && option.dataset.optionValueId === optionId
        );
        return !input || String(variant.options[idx]) === input.dataset.value;
      });
    }

    prepareSelectedPrice(dataHTML, variant) {
      if (!this.explicitSelectionEnabled || !this.hasCompleteExplicitSelection()) return null;
      const target = this.productCardWrapperEle.querySelector(".product-card__price .price__container");
      if (!target) return null;
      const source = dataHTML.querySelector("[data-card-selected-price]");
      if (!source || String(source.dataset.cardPriceVariantId || "") !== String(variant?.id || "")) {
        throw new Error("Product option response is missing the matching variant price");
      }
      const sourcePrice = source.querySelector(".price__container");
      if (variant && !sourcePrice) throw new Error("Product option response is missing formatted variant price");
      const copy = (sourcePrice || source).cloneNode(true);
      const existingContent = target.querySelector(".price__content");
      const newContent = copy.querySelector(".price__content");
      if (existingContent && newContent) newContent.className = existingContent.className;
      return { target, html: copy.innerHTML };
    }

    prepareSelectedLabelPricing(dataHTML, variant) {
      if (!this.explicitSelectionEnabled || !this.hasCompleteExplicitSelection()) return null;
      const target = this.productCardWrapperEle.querySelector(".link-product-variant");
      if (!target?.getAttribute("x-labels-data")) return null;
      const source = dataHTML.querySelector("[data-card-selected-labels]");
      if (!source) throw new Error("Product option response is missing variant label pricing");
      const pricing = JSON.parse(source.textContent);
      const expectedPrice = pricing.hide_price ? 0 : Number(variant?.price || 0);
      const expectedCompareAtPrice = pricing.hide_price ? 0 : Number(variant?.compare_at_price || 0);
      if (String(pricing.variant_id || "") !== String(variant?.id || "") ||
          pricing.price !== expectedPrice || pricing.compare_at_price !== expectedCompareAtPrice ||
          typeof pricing.price_with_currency !== "string" || typeof pricing.sale_amount !== "string") {
        throw new Error("Product option response does not match the selected variant label pricing");
      }
      return pricing;
    }

    clearSelectedLabelPricing() {
      if (!this.explicitSelectionEnabled) return;
      this.updateLabelData(this.productCardWrapperEle, null, {
        price: 0, compare_at_price: 0, price_with_currency: "", sale_amount: "",
      });
    }

    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      this.selectOptionInput(option);
    }

    selectOptionInput(option) {
      const position = option.dataset.optionPosition;
      const idx = position - 1;
      if (this.explicitSelectionEnabled) this.confirmedOptionPositions.add(Number(position));

      this.optionValueInputElements
        .filter((el) => el.dataset.optionPosition === position)
        .forEach((el) => {
          const isTarget = el.dataset.optionValueId === option.dataset.optionValueId;
          el.checked = isTarget;
          el.classList.toggle("selected", isTarget);
          if (isTarget) this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        });

      this.reRenderProductSwatches();
    }

    async reRenderProductSwatches() {
      const requestId = ++this._swatchRequestId;
      this._swatchAbortController?.abort();
      this._swatchAbortController = new AbortController();
      this.selectionPending = true;
      this.selectionVerified = false;
      this.selectionError = false;
      this.syncExplicitSelectionStyles();
      this.publishSelectionState();
      const infoWrapper = this.closest(".product-card__info-wrapper");
      const buyButton = infoWrapper?.querySelector(".product-form__btn-add-to-cart");
      if (buyButton) buyButton.disabled = true;

      const selectedValues = this.optionsSelectedValues.filter(Boolean);
      this.productCardWrapperEle.dataset.listOptionValuesIdSelected = selectedValues.join(",");

      const url = `${this.productUrl}?option_values=${selectedValues.join(",")}&section_id=product-swatch`;
      let dataHTML = ProductCardSwatchOption.cacheData[url];

      try {
        this.clearSelectedLabelPricing();
        if (!dataHTML) {
          const response = await fetch(url, { signal: this._swatchAbortController.signal });
          if (!response.ok) throw new Error(`Product option request failed: ${response.status}`);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
        }
        if (requestId !== this._swatchRequestId || !this.isConnected) return;

        const newVariantInfoEl = dataHTML.querySelector(".current-variant");
        if (!newVariantInfoEl || !newVariantInfoEl.textContent.trim()) throw new Error("Product option response is missing variant data");
        const nextVariant = JSON.parse(newVariantInfoEl.textContent);
        if (this.explicitSelectionEnabled && !this.variantMatchesSelection(nextVariant)) throw new Error("Product option response does not match the selected options");
        const selectedPrice = this.prepareSelectedPrice(dataHTML, nextVariant);
        const selectedLabelPricing = this.prepareSelectedLabelPricing(dataHTML, nextVariant);
        ProductCardSwatchOption.cacheData[url] = dataHTML;

        const root = this.productCardWrapperEle;

        // Update current variant info. An option combination that does not exist has no
        // variant to report, and a stale one here made the quantity selector keep the previous
        // variant's rule (see the swatchChange listener in theme.js) — so fall back to null
        // rather than leaving the old value in place.
        this.currentVariant = nextVariant;
        if (newVariantInfoEl && this.currentVariantEl) {
          this.currentVariantEl.textContent = newVariantInfoEl.textContent;
        }
        // Update current variant pre-order info
        this.updateElements(root, dataHTML, '[type="application/json"][data-type="current-variant-additional-info"]');
        this.updateElements(root, dataHTML, '.product-card__properties-group');

        // Update current bundle item data
        this.updateElements(root, dataHTML, '[type="application/json"][data-type="current-bundle-item"]');

        // Same reason as the choose-option path below: the card's quantity box survives the swatch
        // change, so its quantity-rule tooltip has to be re-synced or it keeps describing the variant
        // rendered on first paint.
        this.updateElements(root, dataHTML, '.quantity-rule-tooltip-anchor');

        if (this.isShowEDT) {
          this.updateElements(root, dataHTML, ".estimate-delivery-card-msg");
        }
        this.updateLabelData(root, dataHTML, selectedLabelPricing);
        if (this.currentVariant) syncSwatchOptionStyles(dataHTML, root);
        this.syncExplicitSelectionStyles();
        this.syncDropdownSelections();
        this.updateImage(dataHTML);
        if (selectedPrice) selectedPrice.target.innerHTML = selectedPrice.html;
        this.dispatchVariantChangeEvent();
        this.selectionPending = false;
        this.selectionVerified = true;
        this.publishSelectionState();
        if (buyButton && buyButton.getAttribute("aria-disabled") !== "true" && !this.explicitSelectionEnabled) buyButton.disabled = false;
      } catch (error) {
        if (requestId !== this._swatchRequestId || !this.isConnected) return;
        this.selectionPending = false;
        this.selectionVerified = false;
        this.selectionError = true;
        this.clearSelectedLabelPricing();
        this.currentVariant = null;
        if (this.currentVariantEl) this.currentVariantEl.textContent = "null";
        this.dispatchVariantChangeEvent();
        this.publishSelectionState();
        if (buyButton) buyButton.disabled = true;
        if (error.name !== "AbortError") console.error(error);
      }
    }

    dispatchVariantChangeEvent() {
      const productCardId = this.productCardWrapperEle.id?.replace("ProductCard_", "") ?? "";
      document.dispatchEvent(
        new CustomEvent(`${PRODUCT_EVENT.swatchChange}${productCardId}`, {
          bubbles: true,
          detail: { productId: this.productId, currentVariant: this.currentVariant },
        })
      );
    }

    updateLabelData(rootEl, dataHTML, selectedPricing = null) {
      const metafieldDataset = dataHTML?.querySelector("#product-swatch")?.dataset.metafieldLabel;
      if (!metafieldDataset && !selectedPricing) return;

      const linkVariantEle = rootEl.querySelector(".link-product-variant");
      if (!linkVariantEle) return;

      const linkLabelsData = linkVariantEle.getAttribute("x-labels-data");
      if (!linkLabelsData) return;

      const currentLabelData = xParseJSON(linkLabelsData);
      if (metafieldDataset) {
        const metafieldDataObj = xParseJSON(metafieldDataset);
        currentLabelData.metafield_label = metafieldDataObj.metafield_label;
      }
      if (selectedPricing) {
        const hidePrice = currentLabelData.hide_price === true || selectedPricing.hide_price === true;
        currentLabelData.price = hidePrice ? 0 : selectedPricing.price;
        currentLabelData.compare_at_price = hidePrice ? 0 : selectedPricing.compare_at_price;
        currentLabelData.price_with_currency = hidePrice ? "" : selectedPricing.price_with_currency;
        currentLabelData.sale_amount = hidePrice ? "" : selectedPricing.sale_amount;
        if (hidePrice) currentLabelData.hide_price = true;
      }

      linkVariantEle.setAttribute("x-labels-data", JSON.stringify(currentLabelData));
      Alpine.store("xLabelsAndBadges")?.load(linkVariantEle);
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldElements = rootEl.querySelectorAll(selector);
      const newElements = dataHTML.querySelectorAll(selector);
      const single = newElements.length === 1;

      oldElements.forEach((oldEl, i) => {
        const newEl = single ? newElements[0] : newElements[i];
        if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
      });
    }

    updateImage(dataHTML) {
      const root = this.productCardWrapperEle;
      const activeSlide = root.querySelector(".slide-item.is-active");
      const newImg = dataHTML.querySelector(".product-card__image");
      const newMediaId = newImg?.dataset.mediaId ?? null;

      if (this.effectImage === "carousel") {
        const target = root.querySelector(`.slide-item[data-media-id="${newMediaId}"]`);
        if (activeSlide && target) {
          activeSlide.classList.remove("is-active");
          target.classList.add("is-active");
          return;
        }
      }

      const img = activeSlide?.querySelector(".product-card__image");
      if (img && newImg) {
        img.setAttribute("srcset", newImg.getAttribute("srcset"));
        img.setAttribute("src", newImg.getAttribute("src"));
        if (activeSlide && this.effectImage === "carousel") {
          activeSlide.dataset.mediaId = newMediaId;
        }
      }
    }
  }

  if (!customElements.get("product-card-swatch-option")) {
    customElements.define("product-card-swatch-option", ProductCardSwatchOption);
  }

  class ChooseOptionElement extends HTMLElement {
    static cacheData = {};

    constructor() {
      super();
      this.optionInputElements = Array.from(this.querySelectorAll(".swatch-option__value"));
      this.optionLabelElements = Array.from(this.querySelectorAll(".swatch-option__label"));
      this.optionsSelectedValues = [];
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.chooseOptionElementId = this.dataset.chooseOptionElementId;

      this.optionInputElements
        .filter((el) => el.classList.contains("selected"))
        .forEach((input) => {
          this.optionsSelectedValues[input.dataset.optionPosition - 1] = input.dataset.optionValueId;
        });

      this.currentVariantInfoEle = this.querySelector(
        '[type="application/json"][data-type="current-variant-info"]'
      );
      this.currentVariantAdditionalInfoEle = this.querySelector(
        '[type="application/json"][data-type="current-variant-additional-info"]'
      );
      this.currentVariant = parseVariantInfo(this.currentVariantInfoEle);
    }

    connectedCallback() {
      const handleClick = this.handleClickOptionLabel.bind(this);
      this.optionLabelElements.forEach((el) => {
        el.addEventListener("click", handleClick);
        el.addEventListener("keyup", (e) => {
          if (e.key === "Enter" || e.key === " ") handleClick(e);
        });
      });
    }

    handleClickOptionLabel(event) {
      event.preventDefault();
      const optionLabel = event.currentTarget;
      const option = optionLabel.previousElementSibling;
      const position = option.dataset.optionPosition;
      const idx = position - 1;

      const selectedValue = this.querySelector(`.seclected-value-${position}`);
      if (selectedValue) selectedValue.textContent = option.dataset.value;

      this.optionInputElements
        .filter((el) => el.dataset.optionPosition === position)
        .forEach((el) => {
          const isTarget = el.dataset.optionValueId === option.dataset.optionValueId;
          el.checked = isTarget;
          el.classList.toggle("selected", isTarget);
          if (isTarget) this.optionsSelectedValues[idx] = el.dataset.optionValueId;
        });

      this.reRenderChooseOption();
    }

    async reRenderChooseOption() {
      const variantPickerEle = this.closest(".variant-picker__wrapper");
      const indexParam = variantPickerEle?.dataset?.indexParam;
      const chooseOptionWrapper = this.closest(".choose-option");
      const buyButton = chooseOptionWrapper?.querySelector(".product-form__btn-add-to-cart");
      if (buyButton) buyButton.disabled = true;

      const url = `${this.productUrl}?option_values=${this.optionsSelectedValues.join(",")}&section_id=choose-option&page=${indexParam}`;
      let dataHTML = ChooseOptionElement.cacheData[url];

      if (!dataHTML) {
        try {
          const response = await fetch(url);
          const text = await response.text();
          dataHTML = new DOMParser().parseFromString(text, "text/html");
          ChooseOptionElement.cacheData[url] = dataHTML;
        } catch (error) {
          console.error(error);
          if (buyButton && !buyButton.ariaDisabled) buyButton.disabled = false;
          return;
        }
      }

      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-option__price-wrapper");
      this.updateElements(chooseOptionWrapper, dataHTML, ".choose-options-content .choose-option__properties-group");
      // The quantity box itself is not re-rendered, so its quantity-rule tooltip would keep
      // describing the variant that happened to be selected on first paint. On a product where only
      // SOME variants carry volume pricing that means the tooltip never appears for the ones that
      // do. The anchor element is always present (empty when the variant has no rule), so syncing
      // its contents is enough — the injected <custom-tooltip> upgrades on insertion.
      this.updateElements(chooseOptionWrapper, dataHTML, ".quantity-rule-tooltip-anchor");

      // Same reasoning as the card above: an unavailable combination must clear the variant,
      // not keep the previous one, or the choose-option quantity box holds a stale rule.
      const newVariantInfoEl = dataHTML.querySelector(
        '[type="application/json"][data-type="current-variant-info"]'
      );
      this.currentVariant = parseVariantInfo(newVariantInfoEl);
      if (newVariantInfoEl && this.currentVariantInfoEle) {
        this.currentVariantInfoEle.textContent = newVariantInfoEl.textContent;
      }

      const newAdditionalInfoEl = dataHTML.querySelector(
        '[type="application/json"][data-type="current-variant-additional-info"]'
      );
      if (this.currentVariantAdditionalInfoEle && newAdditionalInfoEl) {
        this.currentVariantAdditionalInfoEle.textContent = newAdditionalInfoEl.textContent;
      }

      syncSwatchOptionStyles(dataHTML, chooseOptionWrapper);
      // Suppress Safari's ::before recreation transition BEFORE re-enabling or dispatching event.
      // Safari recreates ::before when a button transitions from disabled/aria-disabled → enabled,
      // and plays an unwanted shrink animation (transform: none → scale(0)).
      //
      // TWO cases covered:
      // (a) available → available: button.disabled toggled below (non-aria-disabled)
      // (b) unavailable → available: button has aria-disabled="true" (set by Alpine),
      //     SKIPPED in the if-check below, but Alpine removes aria-disabled in a microtask
      //     triggered by dispatchChangeVariantEvent(). Class must be present at that moment.
      //
      // Timeline:
      //   [sync]      add is-variant-changing to button
      //   [sync]      re-enable if not aria-disabled (case a)
      //   [sync]      dispatchChangeVariantEvent → Alpine microtask queued
      //   [microtask] Alpine removes aria-disabled → ::before created → transition: none ✅ (case b)
      //   [rAF 1]     first frame
      //   [rAF 2]     remove is-variant-changing → transitions restored
      if (buyButton) {
        buyButton.classList.add('is-variant-changing');

        if (!buyButton.ariaDisabled) {
          buyButton.disabled = false;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            buyButton.classList.remove('is-variant-changing');
          });
        });
      }

      syncSwatchOptionStyles(dataHTML, chooseOptionWrapper);
      if (buyButton && !buyButton.ariaDisabled) buyButton.disabled = false;
      this.dispatchChangeVariantEvent();
    }

    updateElements(rootEl, dataHTML, selector) {
      const oldEl = rootEl.querySelector(selector);
      const newEl = dataHTML.querySelector(selector);
      if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
    }

    dispatchChangeVariantEvent() {
      document.dispatchEvent(
        new CustomEvent(`${PRODUCT_EVENT.swatchChange}${this.chooseOptionElementId}`, {
          bubbles: true,
          detail: { productId: this.productId, currentVariant: this.currentVariant },
        })
      );
    }
  }

  if (!customElements.get("choose-option")) {
    customElements.define("choose-option", ChooseOptionElement);
  }
}
